import React from 'react'
import { PROD_APP_URL } from '@/lib/constant'

/**
 * Structured data (JSON-LD) for homepage SEO
 * Implements schema.org markup for better search engine understanding
 */
export function StructuredData() {
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'HeyBoss AI',
    url: PROD_APP_URL,
    logo: `${PROD_APP_URL}/logo.png`,
    description:
      'AI-powered website and app builder. Create professional websites, online stores, and web applications in minutes with no coding required.',
    sameAs: [
      'https://twitter.com/heyboss_ai',
      'https://www.linkedin.com/company/heyboss-ai',
      'https://www.social.com/heyboss.ai',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Support',
      email: 'support@heyboss.ai',
      availableLanguage: [
        'en',
        'zh',
        'ja',
        'ko',
        'es',
        'fr',
        'de',
        'it',
        'pt',
        'ru',
        'ar',
        'hi',
        'id',
        'th',
        'vi',
        'tr',
      ],
    },
  }

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'HeyBoss AI',
    url: PROD_APP_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${PROD_APP_URL}/search?q={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  }

  const softwareApplicationSchema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'HeyBoss AI',
    operatingSystem: 'Web',
    applicationCategory: 'DeveloperApplication',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      description: 'Free tier available with paid plans starting at $20/month',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '5.0',
      ratingCount: '1000',
      bestRating: '5',
      worstRating: '1',
    },
    description:
      'Build professional websites, online stores, and web applications with AI. No coding required. Includes templates, CRM, SEO tools, payment integration, and more.',
    features: [
      'AI-powered website generation',
      'Drag-and-drop editor',
      'E-commerce integration',
      'SEO optimization',
      'Custom domains',
      'Analytics dashboard',
      'Email marketing',
      'CRM tools',
      'Payment processing',
      'Mobile responsive design',
    ],
  }

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: PROD_APP_URL,
      },
    ],
  }

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Do I need coding skills to use HeyBoss?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'No coding skills required! HeyBoss AI lets you build professional websites and apps using simple conversations. Just describe what you want, and our AI builds it for you.',
        },
      },
      {
        '@type': 'Question',
        name: 'How long does it take to build a website?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Most websites can be built in 10-15 minutes. Our AI generates a complete, professional website based on your requirements, and you can customize it further with our intuitive editor.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I use my own domain?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Yes! You can connect your own custom domain to any website built with HeyBoss. We provide easy step-by-step instructions for domain setup.',
        },
      },
      {
        '@type': 'Question',
        name: 'Is there a free plan?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Creating and editing a website with HeyBoss requires credits from a paid plan. See the pricing page for current plans and included features.',
        },
      },
      {
        '@type': 'Question',
        name: 'Can I sell products online?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Absolutely! HeyBoss includes built-in e-commerce features including product catalogs, shopping carts, payment processing with Stripe, and order management.',
        },
      },
    ],
  }

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'AI Website Builder',
    provider: {
      '@type': 'Organization',
      name: 'HeyBoss AI',
    },
    areaServed: 'Worldwide',
    hasOfferCatalog: {
      '@type': 'OfferCatalog',
      name: 'Website Building Services',
      itemListElement: [
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Business Website',
            description:
              'Professional business websites with SEO and analytics',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'E-commerce Store',
            description:
              'Online stores with payment processing and inventory management',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Landing Page',
            description:
              'High-converting landing pages for marketing campaigns',
          },
        },
        {
          '@type': 'Offer',
          itemOffered: {
            '@type': 'Service',
            name: 'Portfolio Website',
            description: 'Showcase your work with beautiful portfolio websites',
          },
        },
      ],
    },
  }

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(organizationSchema),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(websiteSchema),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(softwareApplicationSchema),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbSchema),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(faqSchema),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(serviceSchema),
        }}
      />
    </>
  )
}
